/**
 * Usage → Overview — prototype VIEWS.uover (docs/prototype.html 4162): every client against every product.
 * Each box is the share of plan used and the change on last month; a red ! means the forecast takes it under
 * the low-use line. GET /usage/overview (the grid, already scoped: managers all, a member their own clients),
 * GET /usage/alerts (the "Predicted to fall" count), GET /products (each product's falling line).
 *
 * The ! and the tooltip's 30-day figure are the cell's own `predicted_below_low` / `util_in_30d`: the API leaves
 * both out when the forecast fit is too uneven (ADR §8.3), so the grid never draws a forecast from noise.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Priority, UsageOverview as Overview } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { arrow, groupAlerts, nf, signed, useAlerts, useProducts, useUsageOverview } from '../../lib/usage';
import { Empty, ErrorState, Loading, priorityLabel } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';
import { ClientLogo } from '../clients/parts';
import { UploadModal } from './UploadModal';

type UsageCell = Overview['clients'][number]['cells'][number];

const PW: Record<Priority, number> = { high: 3, med: 2, low: 1 };

/** Prototype cell data-tip: "Pageviews: X of Y. Last month Z. In 30 days about N% of plan." */
function cellTip(metric: string, c: UsageCell): string {
  const f = c.util_in_30d == null ? 'No 30-day forecast yet.' : `In 30 days about ${Math.round(c.util_in_30d)}% of plan.`;
  return `${metric}: ${nf(c.used)} of ${nf(c.plan)}. Last month ${nf(c.base)}. ${f}`;
}

export function UsageOverview() {
  const ov = useUsageOverview();
  const al = useAlerts();
  const pr = useProducts();
  const nav = useNavigate();
  const [upload, setUpload] = useState(false);

  const rows = useMemo(() => [...(ov.data?.clients ?? [])].sort((a, b) => PW[b.client.priority] - PW[a.client.priority]), [ov.data]);
  const keys = useMemo(() => (ov.data?.products ?? []).filter((p) => rows.some((r) => r.cells.some((c) => c.product_key === p.key))), [ov.data, rows]);

  if (ov.isLoading) return <Loading label="Loading usage…" />;
  if (ov.error) return <ErrorState error={ov.error} onRetry={() => void ov.refetch()} />;

  const fallOf = new Map((pr.data ?? []).map((p) => [p.key, p.fall_pct]));
  const cells = rows.flatMap((r) => r.cells);
  const withData = cells.filter((c) => c.util != null);
  const low = withData.filter((c) => c.tone === 'cold').length;
  const fall = withData.filter((c) => c.trend != null && c.trend <= -(fallOf.get(c.product_key) ?? 15)).length;
  const predicted = groupAlerts(al.data ?? []).filter((g) => g.sev !== 'info' && g.predicted).length;
  const miss = cells.filter((c) => c.util == null).length;
  const falls = [...new Set(keys.map((k) => fallOf.get(k.key)).filter((v): v is number => v != null))];
  const open = (id: string) => nav(`/usage/client/${id}`);

  return (
    <>
      <div className="stats">
        <Stat k="Below the low-use line" v={low} s="product uses" cls={low ? 'red' : 'green'} />
        <Stat k="Down on last month" v={fall} s={falls.length <= 1 ? `${falls[0] ?? 15}% or more` : 'past the product’s falling line'} cls={fall ? 'amber' : 'green'} />
        <Stat k="Predicted to fall" v={al.isLoading ? '…' : al.error ? '-' : predicted} s="in the next 30 days" cls={al.isLoading || al.error ? '' : predicted ? 'red' : 'green'} />
        <Stat k="No data yet" v={miss} s="product uses" cls={miss ? 'amber' : 'green'} />
      </div>
      <div className="split" style={{ margin: '2px 0 12px', gap: 14 }}>
        <div className="sub" style={{ fontSize: 13 }}>
          Each box shows the share of plan used, and the change on last month. A <b style={{ color: 'var(--cold)' }}>!</b> means it is predicted to fall under the low-use line. Press a box for
          the full picture.
        </div>
        <div className="grow" />
        <button className="btn" onClick={() => setUpload(true)}>
          Upload usage report
        </button>
      </div>
      {!rows.length ? (
        <div className="panel">
          <Empty title="No clients to show">Clients you look after appear here once they have a product.</Empty>
        </div>
      ) : (
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table className="utab">
            <thead>
              <tr>
                <th>Client</th>
                {keys.map((k) => (
                  <th key={k.key}>{k.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ client: c, cells: cs }) => (
                <tr key={c.id}>
                  <td>
                    <a href={`/usage/client/${c.id}`} className="split" style={{ color: 'inherit', textDecoration: 'none', gap: 10 }} onClick={(e) => (e.preventDefault(), open(c.id))}>
                      <ClientLogo id={c.id} name={c.name} size={30} />
                      <div>
                        <b>{c.name}</b>
                        <div className="sub">
                          {priorityLabel(c.priority)} · SPOC {firstName(c.spoc.name)}
                        </div>
                      </div>
                    </a>
                  </td>
                  {keys.map((k) => {
                    const cell = cs.find((x) => x.product_key === k.key);
                    if (!cell) return <td key={k.key} className="uh none">-</td>;
                    if (cell.util == null)
                      return (
                        <td key={k.key} className="uh">
                          <button className="ucell info" onClick={() => open(c.id)} title={`${k.name}: ${cell.feed_state.label}`}>
                            <b>No data</b>
                          </button>
                        </td>
                      );
                    return (
                      <td key={k.key} className="uh">
                        <button className={`ucell ${cell.tone}`} onClick={() => open(c.id)} title={cellTip(k.main_metric, cell)}>
                          <b>{Math.round(cell.util)}%</b>
                          <i>
                            {arrow(cell.trend)} {signed(cell.trend)}
                          </i>
                          {cell.predicted_below_low ? <em title="Predicted to fall below the low-use line">!</em> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {upload ? <UploadModal onClose={() => setUpload(false)} /> : null}
    </>
  );
}
